export const FULL_REPORT_PROMPT = `Generate an 8,000-word in-depth Human Potential OS™ assessment report.

This must be a true long-form report, not a summary or outline.

Important:
- Do not write one short paragraph per section.
- Each section must contain detailed interpretation.
- Use the actual scores accurately.
- Do not merge states with different scores.
- Separate resource states from blocker states.
- Explain how the highest blocker may override the higher resource states and strongest traits.
- Include real-life examples, professional implications, relationship implications, coaching insights, and action steps.

Use this structure:

1. Opening Overview — 600–800 words
2. Executive Summary — 700–900 words
3. Characteristic Profile Analysis — 1,800–2,200 words
4. Being, Doing, and Having Pattern — 700–900 words
5. Harmonic Scale Analysis — 1,200–1,600 words
6. Resource States vs Blocker States — 700–900 words
7. Harmonic Override Pattern — 700–900 words
8. Trait-Emotion Interaction — 900–1,200 words
9. Key Strengths — 700–900 words
10. Breakthrough Zones — 800–1,000 words
11. Professional Insights — 900–1,200 words
12. Relationship Insights — 900–1,200 words
13. Coach Notes — 700–900 words
14. Action Plan — 700–900 words
15. Conclusion — 400–600 words

If you cannot complete the full 8,000 words in one response due to output limits, generate Part 1 only and end with:
"Continue generating Part 2."

Do not call the report complete until all parts are generated.`;

export const FULL_REPORT_STANDARD_PROMPT = `Generate a comprehensive Human Potential OS™ assessment report based on the respondent's Characteristic Profile and Harmonic Scale Profile.

This must be a rich, detailed, premium-quality analysis, not a short summary.

Write as an expert Human Potential OS™ coach, trainer, and assessment interpreter. The report should help the respondent feel deeply understood while also giving the coach meaningful insight into how to support them.

Use the assessment as a coaching and personal/professional development tool. Do not diagnose, clinically label, or make fixed claims about the person.

Important Harmonic Scale rule:
Do not simply identify the single highest Harmonic score as the dominant state. Separate the Harmonic Scale into:
1. Growth / Resource States: Interest, Optimism, Enthusiasm, Exhilaration.
2. Stabilising State: Rational Neutrality.
3. Blocker / Interference States: Antagonism, Open Anger, Passive Hostility, Fear, Grief, Hopelessness, Apathy.

The highest score from Interest and above shows the respondent's strongest available resource state.
The highest score from Antagonism and below shows the respondent's most important blocker or interference state.

Higher states show potential.
Lower states show what may block that potential.
The highest blocker often explains why the person is not fully living the potential shown by their strongest traits.

When a blocker state is high, explain how it may override, suppress, distort, or drain the person's higher traits and resource states.

The report must include:

1. Opening Overview
2. Executive Summary of the Profile
3. Characteristic Profile Analysis
4. Being, Doing, and Having Pattern
5. Harmonic Scale Analysis
6. Resource States vs Blocker States
7. Harmonic Override Pattern
8. Trait-Emotion Interaction
9. Key Strengths
10. Breakthrough Zones
11. Professional Insights
12. Relationship Insights
13. Coach Notes
14. Action Plan
15. Conclusion

Length: 5,000–8,000 words unless the user requests otherwise.

Use clear headings, warm professional language, and meaningful interpretation. Avoid generic AI language. Avoid shallow trait descriptions.`;

export const QUICK_TOPICS = [
  {
    label: 'Full Report',
    prompt: FULL_REPORT_PROMPT,
    isFullReport: true,
  },
  {
    label: 'Intro to Report',
    prompt: `Generate a warm, professional introduction to this Human Potential OS™ assessment report.

Explain the purpose of the Characteristic Profile, the Harmonic Scale Profile, and how the AI Analysis should be used as a coaching and development tool.

Explain that the Harmonic Scale is interpreted through resource states, blocker states, and the stabilising state of Rational Neutrality.

Make clear that higher states show available potential, while lower states may show what blocks or interferes with that potential.

Do not diagnose or label the respondent.

Length: 700–1,000 words.`,
  },
  {
    label: 'Trait Insights',
    prompt: `Analyze the respondent's Characteristic Profile scores in depth.

Explain the Being, Doing, and Having categories.

Identify the highest and lowest traits.

Interpret each trait in a balanced coaching-oriented way.

Do not just list scores. Explain the pattern.

For each major trait, explain:
- How it may show up at its best.
- How it may show up under pressure.
- How it interacts with other traits.
- How it may be amplified by resource states.
- How it may be suppressed or distorted by blocker states.

Length: 1,500–2,500 words.`,
  },
  {
    label: 'Harmonic Scale',
    prompt: `Analyze the respondent's Harmonic Scale Profile in depth.

Do not simply choose the single highest overall score as the dominant state.

Separate the analysis into:
1. Growth / Resource States: Interest, Optimism, Enthusiasm, Exhilaration.
2. Stabilising State: Rational Neutrality.
3. Blocker / Interference States: Antagonism, Open Anger, Passive Hostility, Fear, Grief, Hopelessness, Apathy.

Identify:
- Highest resource state.
- Highest blocker state.
- Secondary blockers.
- Rational Neutrality.
- Core Harmonic pattern.

Explain that higher states show available potential, while lower states show what may block that potential.

Explain how the highest blocker may override, suppress, distort, or drain the person's higher states and traits.

Use coaching language, not diagnostic language.

Length: 1,500–2,500 words.`,
  },
  {
    label: 'Key Strengths',
    prompt: `Identify the respondent's key strengths based on their highest Characteristic Profile scores and supportive Harmonic resource states.

Explain how these strengths may show up in personal life, professional life, leadership, communication, relationships, and self-development.

Also explain how the highest blocker state may suppress, distort, or drain these strengths under pressure.

Include overuse patterns and practical ways to leverage each strength.

Length: 1,200–2,000 words.`,
  },
  {
    label: 'Breakthrough Insights',
    prompt: `Identify the respondent's main breakthrough opportunities.

Use both the Characteristic Profile and the Harmonic Scale Profile.

Remember:
Higher states show potential.
Lower states show what may block that potential.
The highest blocker from Antagonism and below is one of the most important keys to the profile.

Classify traits into:
- Critical Breakthrough Zone.
- Development Breakthrough Zone.
- Growth / Leverage Zone.

A trait does not need to be low to be a breakthrough area. A high trait can become a breakthrough area if a blocker state suppresses, distorts, or overcharges it.

Explain the main coaching doorway and provide practical recommendations.

Length: 1,500–2,500 words.`,
  },
  {
    label: 'Professional Insights',
    prompt: `Explain how this respondent's profile may show up in professional contexts such as leadership, communication, decision-making, collaboration, productivity, adaptability, influence, conflict, creativity, strategic thinking, business, career, and performance.

Use the highest traits to identify professional strengths.

Use the highest blocker state to explain where those strengths may become inconsistent, reactive, delayed, emotionally charged, or underexpressed.

Provide practical workplace development recommendations.

Length: 1,500–2,500 words.`,
  },
  {
    label: 'Relationship Insights',
    prompt: `Explain how this respondent's profile may influence personal relationships, communication, emotional expression, trust, conflict, connection, boundaries, and relational growth.

Use the highest relationship-relevant traits such as Communication, Sociability, Integrity, Positive Outlook, Judgment, and Adaptability.

Use the highest blocker state to explain how lower emotional states may override or distort otherwise positive relational traits.

Provide supportive, practical recommendations.

Length: 1,500–2,500 words.`,
  },
  {
    label: 'Action Steps',
    prompt: `Create a practical action plan based on the respondent's Characteristic Profile and Harmonic Scale Profile.

Include:
- Immediate 7-day steps.
- 30-day development actions.
- 90-day growth direction.
- Reflection questions.
- Suggested coaching focus.
- Practical exercises.

The action plan should focus on:
1. Leveraging the person's strongest traits.
2. Accessing higher resource states more consistently.
3. Reducing the influence of the highest blocker state.
4. Preventing lower states from overriding positive traits and potential.

Length: 1,200–2,000 words.`,
  },
  {
    label: 'Coach Notes',
    prompt: `Generate coach-facing notes for a trained Human Potential OS™ practitioner.

Identify:
- The main profile story.
- Highest resource state.
- Highest blocker state.
- Secondary blockers.
- Likely coaching themes.
- Useful questions to ask.
- Strengths to leverage.
- Possible blind spots.
- Emotional patterns to approach gently.
- What not to over-assume.
- Suggested first-session structure.
- How to help the client reduce blocker-state interference.
- How to help the client access higher traits and resource states more consistently.

Length: 1,500–2,500 words.`,
  },
];
